const API = process.env.REACT_APP_API_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export interface WalletSummary {
  walletId: string;
  balance: number;
  totalCashbackEarned: number;
  totalMoneyAdded: number;
  totalMoneySpent: number;
  active: boolean;
  updatedAt: string;
}

export interface WalletTransaction {
  id: string;
  type: 'MONEY_ADDED' | 'CASHBACK' | 'ORDER_PAYMENT' | 'REFUND' | 'ADJUSTMENT';
  amount: number;
  balanceAfter: number;
  referenceNumber: string;
  description: string;
  status: string;
  orderId?: string;
  createdAt: string;
}

export interface CashbackSlab {
  id: string;
  minAmount: number;
  maxAmount: number;
  cashbackPercentage: number;
  description: string;
  active: boolean;
  priority: number;
}

export interface CashbackPreview {
  amount: number;
  cashbackPercentage: number;
  cashbackAmount: number;
  totalCredited: number;
}

export const walletService = {
  async getWallet(): Promise<WalletSummary> {
    const res = await fetch(`${API}/api/wallet`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch wallet');
    return res.json();
  },

  async getCashbackSlabs(): Promise<CashbackSlab[]> {
    const res = await fetch(`${API}/api/wallet/cashback-slabs`, { headers: authHeaders() });
    if (!res.ok) return [];
    return res.json();
  },

  async previewCashback(amount: number): Promise<CashbackPreview> {
    const res = await fetch(`${API}/api/wallet/cashback-preview?amount=${amount}`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to preview');
    return res.json();
  },

  async initiateRecharge(amount: number, paymentMethod: string) {
    const res = await fetch(`${API}/api/wallet/recharge/initiate`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ amount, paymentMethod }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to initiate recharge');
    return data;
  },

  async initiateClickPayRecharge(amount: number): Promise<{ rechargeId: string; transactionId: string; redirectUrl: string; amount: number; cashbackAmount: number; totalCredited: number }> {
    const res = await fetch(`${API}/api/wallet/recharge/clickpay`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ amount, paymentMethod: 'clickpay' }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to initiate ClickPay recharge');
    return data;
  },

  async confirmRecharge(rechargeId: string, paymentTransactionId: string): Promise<WalletSummary> {
    const res = await fetch(`${API}/api/wallet/recharge/confirm`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ rechargeId, paymentTransactionId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to confirm recharge');
    return data;
  },

  async getTransactions(): Promise<WalletTransaction[]> {
    const res = await fetch(`${API}/api/wallet/transactions`, { headers: authHeaders() });
    if (!res.ok) return [];
    return res.json();
  },

  async getRecentTransactions(): Promise<WalletTransaction[]> {
    const res = await fetch(`${API}/api/wallet/transactions/recent`, { headers: authHeaders() });
    if (!res.ok) return [];
    return res.json();
  },
};
